import { useMemo } from 'react';
import {
  useClientChurn,
  useCurrentUserProfile,
  useNoShows,
  useRecentVisits,
  useRegularConversion,
  type VisitRow,
} from '@busy/api';

// Coaching Insights — cross-signal pattern detector that translates
// multiple operational numbers into a single "here's what's likely
// going on" insight per pattern matched. Surfaces on the Today screen
// (next to NextMilestonesTile).
//
// Why a rule engine, not ad-hoc components per insight:
//   * The signals interact — "high quality + high churn + low cut
//     times" means something different than each signal alone. A
//     single tile that maps signal-tuples → insights makes the
//     interaction explicit and testable.
//   * New patterns get added as the beta cohort surfaces them. Each
//     new rule is one pure function from Signals → Insight | null —
//     no UI changes needed downstream.
//   * Rules are sorted by priority so when multiple match, the most
//     actionable insight wins the slot. The tile shows ONE insight
//     at a time; users get fatigued by lists of "advice."
//
// What's deliberately NOT in scope for v1:
//   * Per-user dismissals / snoozes. v1 just shows the highest-
//     priority insight every load. v1.1 can add a "remind me later"
//     mechanic once we see how often insights cycle.
//   * Long-form lesson linking. We pass a `lessonKey` per insight,
//     but the UI just shows the body text — wiring to the lessons
//     route is a follow-up.

const LOOKBACK_DAYS = 90;
const MIN_VISITS_FOR_INSIGHTS = 30;

// ────────── Signal shape ──────────

/** The full signal bundle each rule sees. Computed once from the
 *  hooks below + a single pass over recent visits. All fields are
 *  nullable when there's not enough data to back them — rules
 *  handle nulls defensively. */
export type CoachingSignals = {
  // Volume — drives the "enough data?" gate
  totalVisits: number;
  // Quality (1-3 self-rating, migration 0075)
  avgQuality: number | null;
  nailedItPct: number | null;
  offDayPct: number | null;
  qualitySampleSize: number;
  // Operational
  avgCutMinutes: number | null;
  avgPausePerCut: number | null;
  // Retention
  churnPct: number | null;
  conversionPct: number | null;
  // Acquisition
  recentNewClientCount: number; // distinct clients with first visit in window
  // No-shows
  noShowCount: number;
  // Revenue & ticket
  avgTicketCents: number | null;
  // Tips
  tipRatePct: number | null;       // tips ÷ revenue × 100
  tipAttachRatePct: number | null; // % of cuts that received any tip
  // Mix
  walkinPct: number | null;        // walkin / (walkin + appointment) × 100, null when no tagging
  walkinAppointmentTagged: number;
  // Add-ons
  addonAttachPct: number | null;
  // Per-phase signals (migration 0075 — phase_times jsonb on visits)
  /** Average seconds spent on the "consult" phase per visit, when a
   *  consult phase exists. Null when no phase_times have been logged
   *  (single-phase services, anonymous walk-ins, pre-0075 visits). */
  avgConsultSec: number | null;
  consultSampleSize: number;
  // Cut time variance — coefficient of variation (stddev ÷ mean).
  // Higher = more inconsistent cut times. > 0.40 typically signals
  // distraction or unreliable scheduling.
  cutTimeCv: number | null;
  // Service-mix concentration — top cut type as % of total cuts.
  // > 70% means the barber is one service deep and exposed to demand
  // shifts in that one style. Null when there are no cut types named.
  topCutTypePct: number | null;
  topCutTypeName: string | null;
  // Payment-mix concentration — cash share of revenue. High cash is
  // a tax / 1099 / banking visibility issue, not a quality issue.
  cashPct: number | null;
  paymentTagged: number; // count of visits with a payment_method set
  /** Utilization % over the lookback window. chair_time_min ÷ target
   *  hours over the same window × 100. Null when target_hours_per_week
   *  isn't set on the profile. Below 60 = light book; above 90 = at
   *  capacity; above 100 = exceeding target. */
  utilizationPct: number | null;
  /** Discount given as % of revenue. Sum of (default_price - price)
   *  across visits where default > price, divided by total revenue.
   *  Captures the cost of unprompted discounting. */
  discountGivenPct: number | null;
};

// ────────── Insight shape ──────────

export type InsightTone = 'opportunity' | 'concern' | 'celebration';

export type CoachingInsight = {
  /** Stable identifier — used as React key + for analytics. */
  pattern: string;
  priority: number;
  tone: InsightTone;
  titleKey: string;
  bodyKey: string;
  /** Optional bullet list. Each item is an i18n key. Caller can
   *  interpolate the same `vars` into bullet keys. */
  bulletKeys?: string[];
  /** Optional CTA copy for a "see the lesson" affordance. Reserved
   *  for v1.1 — v1 component just shows the body. */
  lessonKey?: string;
  /** Variables interpolated into all i18n strings on render. */
  vars?: Record<string, string | number>;
};

// ────────── Rule implementations ──────────

type Rule = (signals: CoachingSignals) => CoachingInsight | null;

/** PATTERN 1: Self-perception gap
 *
 *  High self-rated quality + high churn + low cut times = the barber
 *  thinks they're nailing it, but clients vote with their feet. The
 *  data says they're rushing through the experience. The most common
 *  growth-ceiling pattern for a fast-cutting barber.
 *
 *  Thresholds (heuristic — tune from beta feedback):
 *   - avg quality ≥ 2.5 OR nailed-it pct ≥ 60%
 *   - churn pct ≥ 30%
 *   - avg cut minutes < 25
 *   - quality sample size ≥ 10 (don't trigger on thin data) */
const selfPerceptionGapRule: Rule = (s) => {
  if (s.qualitySampleSize < 10) return null;
  if (s.churnPct == null || s.churnPct < 30) return null;
  if (s.avgCutMinutes == null || s.avgCutMinutes >= 25) return null;
  const highQuality =
    (s.avgQuality != null && s.avgQuality >= 2.5) ||
    (s.nailedItPct != null && s.nailedItPct >= 60);
  if (!highQuality) return null;
  return {
    pattern: 'self_perception_gap',
    priority: 90,
    tone: 'concern',
    titleKey: 'insight.selfPerceptionGap.title',
    bodyKey: 'insight.selfPerceptionGap.body',
    bulletKeys: [
      'insight.selfPerceptionGap.bullet.rushed',
      'insight.selfPerceptionGap.bullet.rebook',
      'insight.selfPerceptionGap.bullet.consult',
    ],
    lessonKey: 'insight.selfPerceptionGap.lesson',
    vars: {
      nailedPct: Math.round(s.nailedItPct ?? 0),
      churnPct: Math.round(s.churnPct ?? 0),
      avgMin: Math.round(s.avgCutMinutes ?? 0),
    },
  };
};

/** PATTERN 2: Honest gap
 *
 *  High acquisition + high churn + low self-rated quality. Barber
 *  is acquiring clients and being self-aware that something's off
 *  with the cuts. Coaching surface: encourage focused technique
 *  practice on the cut type they rate lowest on.
 *
 *  Thresholds:
 *   - churn pct ≥ 30%
 *   - recent new clients ≥ 10 (matches the threshold dashboard uses)
 *   - avg quality ≤ 1.8 OR off-day pct ≥ 30%
 *   - quality sample size ≥ 10 */
const honestGapRule: Rule = (s) => {
  if (s.qualitySampleSize < 10) return null;
  if (s.recentNewClientCount < 10) return null;
  if (s.churnPct == null || s.churnPct < 30) return null;
  const lowQuality =
    (s.avgQuality != null && s.avgQuality <= 1.8) ||
    (s.offDayPct != null && s.offDayPct >= 30);
  if (!lowQuality) return null;
  return {
    pattern: 'honest_gap',
    priority: 85,
    tone: 'opportunity',
    titleKey: 'insight.honestGap.title',
    bodyKey: 'insight.honestGap.body',
    bulletKeys: [
      'insight.honestGap.bullet.identify',
      'insight.honestGap.bullet.drill',
      'insight.honestGap.bullet.measure',
    ],
    lessonKey: 'insight.honestGap.lesson',
    vars: {
      newClients: s.recentNewClientCount,
      churnPct: Math.round(s.churnPct ?? 0),
      offDayPct: Math.round(s.offDayPct ?? 0),
    },
  };
};

/** PATTERN 3: Strong cuts, weak rebooks
 *
 *  Quality is solid (≥ 2.3 avg) but conversion is poor (< 50%). The
 *  cuts are landing — clients just aren't being asked to book the
 *  next one before they leave the chair. This is the most common
 *  "I'm a good barber but my book is empty" pattern.
 *
 *  Lower priority than the gap patterns since the diagnosis is
 *  narrower (just needs the rebook ask). */
const strongCutsWeakRebooksRule: Rule = (s) => {
  if (s.qualitySampleSize < 10) return null;
  if (s.avgQuality == null || s.avgQuality < 2.3) return null;
  if (s.conversionPct == null || s.conversionPct >= 50) return null;
  return {
    pattern: 'strong_cuts_weak_rebooks',
    priority: 80,
    tone: 'concern',
    titleKey: 'insight.strongCutsWeakRebooks.title',
    bodyKey: 'insight.strongCutsWeakRebooks.body',
    bulletKeys: [
      'insight.strongCutsWeakRebooks.bullet.ask',
      'insight.strongCutsWeakRebooks.bullet.script',
      'insight.strongCutsWeakRebooks.bullet.calendar',
    ],
    lessonKey: 'insight.strongCutsWeakRebooks.lesson',
    vars: {
      avgQ: (s.avgQuality ?? 0).toFixed(1).replace(/\.0$/, ''),
      conversionPct: Math.round(s.conversionPct ?? 0),
    },
  };
};

/** PATTERN 4: High tips, weak retention
 *
 *  Clients tip generously (rate ≥ 20%) but don't return (churn ≥
 *  30%). The barber's energy / personality / atmosphere is winning,
 *  but the commitment ask is missing. Easy to fix — the relationship
 *  capital is already there, just needs the convert-to-rebook step. */
const highTipsWeakRetentionRule: Rule = (s) => {
  if (s.totalVisits < 30) return null;
  if (s.tipRatePct == null || s.tipRatePct < 20) return null;
  if (s.churnPct == null || s.churnPct < 30) return null;
  return {
    pattern: 'high_tips_weak_retention',
    priority: 78,
    tone: 'concern',
    titleKey: 'insight.highTipsWeakRetention.title',
    bodyKey: 'insight.highTipsWeakRetention.body',
    bulletKeys: [
      'insight.highTipsWeakRetention.bullet.warmth',
      'insight.highTipsWeakRetention.bullet.convert',
      'insight.highTipsWeakRetention.bullet.text',
    ],
    lessonKey: 'insight.highTipsWeakRetention.lesson',
    vars: {
      tipRate: Math.round(s.tipRatePct ?? 0),
      churnPct: Math.round(s.churnPct ?? 0),
    },
  };
};

/** PATTERN 5: Walk-in dependency
 *
 *  Walk-in mix is extreme (≥ 90%) AND churn is high (≥ 30%). The
 *  business is one-and-done: clients come in once, get a fine cut,
 *  never come back. Rebook discipline is the lever. 90% threshold
 *  (vs softer 70%) makes this a "dependency" — at 70% the barber
 *  still has a meaningful appointment book; at 90% they really are
 *  riding walk-ins. */
const walkinDependencyRule: Rule = (s) => {
  if (s.walkinAppointmentTagged < 15) return null; // need enough tagged data
  if (s.walkinPct == null || s.walkinPct < 90) return null;
  if (s.churnPct == null || s.churnPct < 30) return null;
  return {
    pattern: 'walkin_dependency',
    priority: 75,
    tone: 'concern',
    titleKey: 'insight.walkinDependency.title',
    bodyKey: 'insight.walkinDependency.body',
    bulletKeys: [
      'insight.walkinDependency.bullet.exit',
      'insight.walkinDependency.bullet.card',
      'insight.walkinDependency.bullet.window',
    ],
    lessonKey: 'insight.walkinDependency.lesson',
    vars: {
      walkinPct: Math.round(s.walkinPct ?? 0),
      churnPct: Math.round(s.churnPct ?? 0),
    },
  };
};

/** PATTERN 6: Frequent no-shows
 *
 *  5+ no-shows logged in the last 90 days. Confirmation texts cut
 *  no-shows by ~50% in retail-service studies. Single-lever fix —
 *  text 24h ahead. */
const frequentNoShowsRule: Rule = (s) => {
  if (s.noShowCount < 5) return null;
  return {
    pattern: 'frequent_no_shows',
    priority: 72,
    tone: 'concern',
    titleKey: 'insight.frequentNoShows.title',
    bodyKey: 'insight.frequentNoShows.body',
    bulletKeys: [
      'insight.frequentNoShows.bullet.confirm',
      'insight.frequentNoShows.bullet.script',
      'insight.frequentNoShows.bullet.repeat',
    ],
    lessonKey: 'insight.frequentNoShows.lesson',
    vars: {
      noShows: s.noShowCount,
    },
  };
};

/** PATTERN 7: Underpriced craftsman
 *
 *  Quality is high (≥ 2.5 avg) but avg ticket is below $40 — below
 *  the typical barber tier. Quality this consistent supports a
 *  pricing conversation. Most common pattern for newer barbers who
 *  haven't raised prices since launching. */
const underpricedCraftsmanRule: Rule = (s) => {
  if (s.qualitySampleSize < 15) return null;
  if (s.avgQuality == null || s.avgQuality < 2.5) return null;
  if (s.avgTicketCents == null || s.avgTicketCents >= 4000) return null;
  return {
    pattern: 'underpriced_craftsman',
    priority: 78,
    tone: 'opportunity',
    titleKey: 'insight.underpricedCraftsman.title',
    bodyKey: 'insight.underpricedCraftsman.body',
    bulletKeys: [
      'insight.underpricedCraftsman.bullet.benchmark',
      'insight.underpricedCraftsman.bullet.cadence',
      'insight.underpricedCraftsman.bullet.signal',
    ],
    lessonKey: 'insight.underpricedCraftsman.lesson',
    vars: {
      avgQ: (s.avgQuality ?? 0).toFixed(1).replace(/\.0$/, ''),
      avgTicket: Math.round((s.avgTicketCents ?? 0) / 100),
    },
  };
};

/** PATTERN 8: Add-on opportunity
 *
 *  Add-on attach rate is low (< 15%) with enough sample (≥ 20 cuts).
 *  Even adding one extra service per 10 cuts measurably moves
 *  revenue without changing schedule density. */
const addOnOpportunityRule: Rule = (s) => {
  if (s.totalVisits < 20) return null;
  if (s.addonAttachPct == null || s.addonAttachPct >= 15) return null;
  // Don't fire if quality is low — that's a different (deeper)
  // conversation than upsell discipline.
  if (s.avgQuality != null && s.avgQuality < 2.0) return null;
  return {
    pattern: 'addon_opportunity',
    priority: 60,
    tone: 'opportunity',
    titleKey: 'insight.addOnOpportunity.title',
    bodyKey: 'insight.addOnOpportunity.body',
    bulletKeys: [
      'insight.addOnOpportunity.bullet.beard',
      'insight.addOnOpportunity.bullet.product',
      'insight.addOnOpportunity.bullet.bundle',
    ],
    lessonKey: 'insight.addOnOpportunity.lesson',
    vars: {
      attachPct: Math.round(s.addonAttachPct ?? 0),
    },
  };
};

/** PATTERN 9: Quiet pro
 *
 *  Strong retention (churn < 15%) + high quality (≥ 2.4 avg) but
 *  low acquisition (< 5 new clients in window). The work is great,
 *  the loyal clientele is locked in — the growth lever is marketing
 *  / referral discipline, not skill or service. */
const quietProRule: Rule = (s) => {
  if (s.qualitySampleSize < 15) return null;
  if (s.avgQuality == null || s.avgQuality < 2.4) return null;
  if (s.churnPct == null || s.churnPct >= 15) return null;
  if (s.recentNewClientCount >= 5) return null;
  return {
    pattern: 'quiet_pro',
    priority: 65,
    tone: 'opportunity',
    titleKey: 'insight.quietPro.title',
    bodyKey: 'insight.quietPro.body',
    bulletKeys: [
      'insight.quietPro.bullet.referral',
      'insight.quietPro.bullet.social',
      'insight.quietPro.bullet.review',
    ],
    lessonKey: 'insight.quietPro.lesson',
    vars: {
      churnPct: Math.round(s.churnPct ?? 0),
      avgQ: (s.avgQuality ?? 0).toFixed(1).replace(/\.0$/, ''),
      newClients: s.recentNewClientCount,
    },
  };
};

/** PATTERN 10: Distracted (cut-timer interruptions + cut-time variance)
 *
 *  Two ways to trigger: high pause count per cut (≥ 1.5) OR high
 *  cut-time variance (CV ≥ 0.40, meaning stddev is at least 40% of
 *  the mean — your cut times bounce around).
 *
 *  Both root-cause to the same thing: lack of repeatable rhythm.
 *  Either interruptions break it OR cut times are inconsistent
 *  enough that the barber can't predict their day. Either way:
 *  mechanical fix, not motivational.
 *
 *  The body copy adapts to which trigger fired so the diagnosis is
 *  honest about what the data shows. */
const distractedRule: Rule = (s) => {
  if (s.totalVisits < 20) return null;
  const highPauses = s.avgPausePerCut != null && s.avgPausePerCut >= 1.5;
  const highVariance = s.cutTimeCv != null && s.cutTimeCv >= 0.4;
  if (!highPauses && !highVariance) return null;
  return {
    pattern: 'distracted',
    priority: 55,
    tone: 'concern',
    titleKey: 'insight.distracted.title',
    // Pick the body copy that matches which trigger fired. When BOTH
    // fire, the combined version reads more honestly.
    bodyKey:
      highPauses && highVariance
        ? 'insight.distracted.body.both'
        : highPauses
          ? 'insight.distracted.body.pauses'
          : 'insight.distracted.body.variance',
    bulletKeys: [
      'insight.distracted.bullet.silent',
      'insight.distracted.bullet.zone',
      'insight.distracted.bullet.queue',
    ],
    lessonKey: 'insight.distracted.lesson',
    vars: {
      pausesPerCut: (s.avgPausePerCut ?? 0).toFixed(1).replace(/\.0$/, ''),
      cvPct: Math.round((s.cutTimeCv ?? 0) * 100),
    },
  };
};

/** PATTERN 11: Cracking the code (celebration)
 *
 *  All three core metrics are strong: quality ≥ 2.5, conversion ≥
 *  60%, tip rate ≥ 18%. The barber has the system dialed in. This
 *  is the "scale what's working" moment — protect the rhythm,
 *  consider price discipline, plan for capacity. */
const crackingTheCodeRule: Rule = (s) => {
  if (s.qualitySampleSize < 20) return null;
  if (s.avgQuality == null || s.avgQuality < 2.5) return null;
  if (s.conversionPct == null || s.conversionPct < 60) return null;
  if (s.tipRatePct == null || s.tipRatePct < 18) return null;
  return {
    pattern: 'cracking_the_code',
    priority: 95, // celebrations win when they trigger — earned recognition matters
    tone: 'celebration',
    titleKey: 'insight.crackingTheCode.title',
    bodyKey: 'insight.crackingTheCode.body',
    bulletKeys: [
      'insight.crackingTheCode.bullet.protect',
      'insight.crackingTheCode.bullet.price',
      'insight.crackingTheCode.bullet.refer',
    ],
    lessonKey: 'insight.crackingTheCode.lesson',
    vars: {
      avgQ: (s.avgQuality ?? 0).toFixed(1).replace(/\.0$/, ''),
      conversionPct: Math.round(s.conversionPct ?? 0),
      tipRate: Math.round(s.tipRatePct ?? 0),
    },
  };
};

/** PATTERN 12: Talks too much
 *
 *  Consult phase averages > 4 minutes AND conversion is mediocre
 *  (< 50%). The barber is spending lots of time talking, not enough
 *  closing — clients leave feeling chatted-up but never get the
 *  rebook ask. Different from "strong cuts weak rebooks" because
 *  the lever here is specifically the consult: tighten it, end
 *  with a close. */
const talksTooMuchRule: Rule = (s) => {
  if (s.consultSampleSize < 10) return null;
  if (s.avgConsultSec == null || s.avgConsultSec < 240) return null;
  if (s.conversionPct == null || s.conversionPct >= 50) return null;
  return {
    pattern: 'talks_too_much',
    priority: 70,
    tone: 'concern',
    titleKey: 'insight.talksTooMuch.title',
    bodyKey: 'insight.talksTooMuch.body',
    bulletKeys: [
      'insight.talksTooMuch.bullet.bookend',
      'insight.talksTooMuch.bullet.tighten',
      'insight.talksTooMuch.bullet.close',
    ],
    lessonKey: 'insight.talksTooMuch.lesson',
    vars: {
      consultMin: ((s.avgConsultSec ?? 0) / 60).toFixed(1).replace(/\.0$/, ''),
      conversionPct: Math.round(s.conversionPct ?? 0),
    },
  };
};

/** PATTERN 13: Personality wins
 *
 *  Self-rated quality is low (avg ≤ 1.8 OR off-day ≥ 30%) but tip
 *  rate is high (≥ 20%). The barber is winning on personality /
 *  atmosphere / energy but is honest that the cut work isn't there
 *  yet. Genuinely an opportunity: people already like them; the
 *  cut technique is the gap to close.
 *
 *  This is the "great host, weaker chef" diagnosis. Opportunity
 *  tone, not concern — the foundation (people liking you) is the
 *  hardest part already done. */
const personalityWinsRule: Rule = (s) => {
  if (s.qualitySampleSize < 15) return null;
  if (s.tipRatePct == null || s.tipRatePct < 20) return null;
  const lowQuality =
    (s.avgQuality != null && s.avgQuality <= 1.8) ||
    (s.offDayPct != null && s.offDayPct >= 30);
  if (!lowQuality) return null;
  return {
    pattern: 'personality_wins',
    priority: 68,
    tone: 'opportunity',
    titleKey: 'insight.personalityWins.title',
    bodyKey: 'insight.personalityWins.body',
    bulletKeys: [
      'insight.personalityWins.bullet.foundation',
      'insight.personalityWins.bullet.drill',
      'insight.personalityWins.bullet.combine',
    ],
    lessonKey: 'insight.personalityWins.lesson',
    vars: {
      tipRate: Math.round(s.tipRatePct ?? 0),
      offDayPct: Math.round(s.offDayPct ?? 0),
    },
  };
};

/** PATTERN 14: Appointment-driven (celebration)
 *
 *  80%+ appointments + low churn (< 20%) + reasonable quality (≥
 *  2.2). The booking discipline is real — they've built a
 *  predictable book with a loyal clientele. Celebrate it but also
 *  surface the next move (referral capacity, pricing). */
const appointmentDominanceRule: Rule = (s) => {
  if (s.walkinAppointmentTagged < 20) return null;
  if (s.walkinPct == null || s.walkinPct >= 20) return null; // 80%+ appt = walk-in pct < 20
  if (s.churnPct == null || s.churnPct >= 20) return null;
  if (s.avgQuality != null && s.avgQuality < 2.2) return null;
  return {
    pattern: 'appointment_dominance',
    priority: 88,
    tone: 'celebration',
    titleKey: 'insight.appointmentDominance.title',
    bodyKey: 'insight.appointmentDominance.body',
    bulletKeys: [
      'insight.appointmentDominance.bullet.protect',
      'insight.appointmentDominance.bullet.price',
      'insight.appointmentDominance.bullet.referral',
    ],
    lessonKey: 'insight.appointmentDominance.lesson',
    vars: {
      apptPct: Math.round(100 - (s.walkinPct ?? 0)),
      churnPct: Math.round(s.churnPct ?? 0),
    },
  };
};

/** PATTERN 15: Service concentration
 *
 *  Top cut type is > 70% of all cuts. One-trick-pony risk: if that
 *  style falls out of fashion or the barber gets bored with it,
 *  the business has no fallback. Lower priority because some
 *  specialization is healthy — only fires above 70% which is true
 *  over-concentration. */
const serviceConcentrationRule: Rule = (s) => {
  if (s.totalVisits < 30) return null;
  if (s.topCutTypePct == null || s.topCutTypePct < 70) return null;
  if (s.topCutTypeName == null) return null;
  return {
    pattern: 'service_concentration',
    priority: 50,
    tone: 'opportunity',
    titleKey: 'insight.serviceConcentration.title',
    bodyKey: 'insight.serviceConcentration.body',
    bulletKeys: [
      'insight.serviceConcentration.bullet.adjacent',
      'insight.serviceConcentration.bullet.bundle',
      'insight.serviceConcentration.bullet.signature',
    ],
    lessonKey: 'insight.serviceConcentration.lesson',
    vars: {
      topPct: Math.round(s.topCutTypePct ?? 0),
      topName: s.topCutTypeName ?? '',
    },
  };
};

/** PATTERN 16: Cash dominance
 *
 *  Cash > 80% of tagged-payment revenue. Tax / banking / loan-
 *  visibility issue, not a quality issue. Heavy cash means: harder
 *  to prove income for credit, more theft / loss risk, less
 *  visibility into the business performance. Card/digital methods
 *  give a paper trail that compounds in value over years.
 *
 *  Requires ≥ 10 payment-tagged visits to fire — without that we
 *  don't have enough sample to assert "80%". */
const cashDominanceRule: Rule = (s) => {
  if (s.paymentTagged < 10) return null;
  if (s.cashPct == null || s.cashPct < 80) return null;
  return {
    pattern: 'cash_dominance',
    priority: 45,
    tone: 'opportunity',
    titleKey: 'insight.cashDominance.title',
    bodyKey: 'insight.cashDominance.body',
    bulletKeys: [
      'insight.cashDominance.bullet.tax',
      'insight.cashDominance.bullet.trail',
      'insight.cashDominance.bullet.deposit',
    ],
    lessonKey: 'insight.cashDominance.lesson',
    vars: {
      cashPct: Math.round(s.cashPct ?? 0),
    },
  };
};

/** PATTERN 17: Underutilized excellence
 *
 *  Capacity < 60% + quality ≥ 2.4 + churn < 20%. The work is great
 *  and the loyal book stays — the chair just isn't filled enough.
 *  Pure acquisition / marketing opportunity, not a skill problem. */
const underutilizedExcellenceRule: Rule = (s) => {
  if (s.qualitySampleSize < 15) return null;
  if (s.utilizationPct == null || s.utilizationPct >= 60) return null;
  if (s.avgQuality == null || s.avgQuality < 2.4) return null;
  if (s.churnPct == null || s.churnPct >= 20) return null;
  return {
    pattern: 'underutilized_excellence',
    priority: 72,
    tone: 'opportunity',
    titleKey: 'insight.underutilizedExcellence.title',
    bodyKey: 'insight.underutilizedExcellence.body',
    bulletKeys: [
      'insight.underutilizedExcellence.bullet.acquisition',
      'insight.underutilizedExcellence.bullet.referral',
      'insight.underutilizedExcellence.bullet.fillSlots',
    ],
    lessonKey: 'insight.underutilizedExcellence.lesson',
    vars: {
      utilPct: Math.round(s.utilizationPct ?? 0),
      avgQ: (s.avgQuality ?? 0).toFixed(1).replace(/\.0$/, ''),
      churnPct: Math.round(s.churnPct ?? 0),
    },
  };
};

/** PATTERN 18: At-capacity — time to raise prices
 *
 *  Utilization ≥ 90% + churn < 15% + quality ≥ 2.3. The chair is
 *  full of loyal clients. The only growth lever left is pricing.
 *  Most barbers wait too long to use it. */
const atCapacityRaisePricesRule: Rule = (s) => {
  if (s.qualitySampleSize < 15) return null;
  if (s.utilizationPct == null || s.utilizationPct < 90) return null;
  if (s.churnPct == null || s.churnPct >= 15) return null;
  if (s.avgQuality == null || s.avgQuality < 2.3) return null;
  return {
    pattern: 'at_capacity_raise_prices',
    priority: 82,
    tone: 'opportunity',
    titleKey: 'insight.atCapacityRaisePrices.title',
    bodyKey: 'insight.atCapacityRaisePrices.body',
    bulletKeys: [
      'insight.atCapacityRaisePrices.bullet.lever',
      'insight.atCapacityRaisePrices.bullet.cadence',
      'insight.atCapacityRaisePrices.bullet.notice',
    ],
    lessonKey: 'insight.atCapacityRaisePrices.lesson',
    vars: {
      utilPct: Math.round(s.utilizationPct ?? 0),
      churnPct: Math.round(s.churnPct ?? 0),
    },
  };
};

/** PATTERN 19: Overworked
 *
 *  Utilization ≥ 100% AND quality is slipping (low avg OR high
 *  off-day rate). The barber is past their target hours AND the
 *  cut work shows it — the math is telling them to slow down
 *  before the book slows down for them. */
const overworkedRule: Rule = (s) => {
  if (s.qualitySampleSize < 15) return null;
  if (s.utilizationPct == null || s.utilizationPct < 100) return null;
  const qualityStruggling =
    (s.avgQuality != null && s.avgQuality < 2.2) ||
    (s.offDayPct != null && s.offDayPct >= 20);
  if (!qualityStruggling) return null;
  return {
    pattern: 'overworked',
    priority: 92, // high — sustainability matters more than other concerns
    tone: 'concern',
    titleKey: 'insight.overworked.title',
    bodyKey: 'insight.overworked.body',
    bulletKeys: [
      'insight.overworked.bullet.price',
      'insight.overworked.bullet.hours',
      'insight.overworked.bullet.protect',
    ],
    lessonKey: 'insight.overworked.lesson',
    vars: {
      utilPct: Math.round(s.utilizationPct ?? 0),
      offDayPct: Math.round(s.offDayPct ?? 0),
    },
  };
};

/** PATTERN 20: Discount drift
 *
 *  Discount given % of revenue > 8%. Discounting more than ~8% of
 *  revenue away is rarely strategic by the time it gets noticed —
 *  it's usually a habit that crept in. Surface for examination. */
const discountDriftRule: Rule = (s) => {
  if (s.totalVisits < 20) return null;
  if (s.discountGivenPct == null || s.discountGivenPct < 8) return null;
  return {
    pattern: 'discount_drift',
    priority: 58,
    tone: 'opportunity',
    titleKey: 'insight.discountDrift.title',
    bodyKey: 'insight.discountDrift.body',
    bulletKeys: [
      'insight.discountDrift.bullet.audit',
      'insight.discountDrift.bullet.intentional',
      'insight.discountDrift.bullet.firm',
    ],
    lessonKey: 'insight.discountDrift.lesson',
    vars: {
      discountPct: Math.round(s.discountGivenPct ?? 0),
    },
  };
};

/** PATTERN 21: Tip-rate ceiling
 *
 *  Tip rate > 25% consistently. That's well above the typical 15-20%
 *  range — clients are tipping unusually high. This is often a soft
 *  market signal that the base price is too low for the value
 *  delivered. Not a guaranteed read, but worth a pricing conversation. */
const tipCeilingRule: Rule = (s) => {
  if (s.totalVisits < 30) return null;
  if (s.tipRatePct == null || s.tipRatePct < 25) return null;
  // Don't fire alongside underpriced_craftsman — those overlap and
  // we want the more specific diagnosis to win.
  if (
    s.qualitySampleSize >= 15 &&
    s.avgQuality != null &&
    s.avgQuality >= 2.5 &&
    s.avgTicketCents != null &&
    s.avgTicketCents < 4000
  ) {
    return null;
  }
  return {
    pattern: 'tip_ceiling',
    priority: 53,
    tone: 'opportunity',
    titleKey: 'insight.tipCeiling.title',
    bodyKey: 'insight.tipCeiling.body',
    bulletKeys: [
      'insight.tipCeiling.bullet.signal',
      'insight.tipCeiling.bullet.test',
      'insight.tipCeiling.bullet.value',
    ],
    lessonKey: 'insight.tipCeiling.lesson',
    vars: {
      tipRate: Math.round(s.tipRatePct ?? 0),
    },
  };
};

/** PATTERN 22: Pro mode (celebration)
 *
 *  The unicorn pattern: appointment-heavy book + high quality +
 *  low churn + working AT or UNDER target hours (utilization ≤
 *  100%). This is what the tool is built to help barbers achieve:
 *  high price, full book, sustainable pace, loyal clients.
 *
 *  Highest priority of any rule — earned recognition for this
 *  combination should always win the slot. */
const proModeRule: Rule = (s) => {
  if (s.qualitySampleSize < 20) return null;
  if (s.walkinAppointmentTagged < 20) return null;
  if (s.walkinPct == null || s.walkinPct >= 25) return null; // 75%+ appt
  if (s.avgQuality == null || s.avgQuality < 2.5) return null;
  if (s.churnPct == null || s.churnPct >= 15) return null;
  if (s.utilizationPct == null) return null;
  if (s.utilizationPct > 100) return null; // sustainable, not overworked
  if (s.utilizationPct < 70) return null;   // and actually busy
  return {
    pattern: 'pro_mode',
    priority: 99, // highest — supersedes every other pattern when matched
    tone: 'celebration',
    titleKey: 'insight.proMode.title',
    bodyKey: 'insight.proMode.body',
    bulletKeys: [
      'insight.proMode.bullet.protect',
      'insight.proMode.bullet.compound',
      'insight.proMode.bullet.mentor',
    ],
    lessonKey: 'insight.proMode.lesson',
    vars: {
      utilPct: Math.round(s.utilizationPct ?? 0),
      apptPct: Math.round(100 - (s.walkinPct ?? 0)),
      churnPct: Math.round(s.churnPct ?? 0),
      avgQ: (s.avgQuality ?? 0).toFixed(1).replace(/\.0$/, ''),
    },
  };
};

const RULES: Rule[] = [
  // Celebrations first — earned recognition pre-empts diagnoses.
  // proMode is the king — when it matches, nothing else matters.
  proModeRule,
  crackingTheCodeRule,
  appointmentDominanceRule,
  // Concerns — sorted roughly by how widely applicable they are
  selfPerceptionGapRule,
  strongCutsWeakRebooksRule,
  overworkedRule,         // sustainability concern — priority 92
  highTipsWeakRetentionRule,
  walkinDependencyRule,
  frequentNoShowsRule,
  talksTooMuchRule,
  distractedRule,
  // Opportunities
  atCapacityRaisePricesRule, // priority 82
  underpricedCraftsmanRule,
  underutilizedExcellenceRule, // priority 72
  honestGapRule,
  personalityWinsRule,
  quietProRule,
  addOnOpportunityRule,
  discountDriftRule,
  tipCeilingRule,
  serviceConcentrationRule,
  cashDominanceRule,
];

// ────────── Compute signals from visits + view hooks ──────────

/** Pure helper — visits + churn + conversion → signals. Exported for
 *  testing; the hook below wraps it with the data fetches.
 *
 *  `targetHoursPerWeek` is optional — when null the utilization signal
 *  is left null (we can't measure utilization without a target). Most
 *  rules that touch utilization gate on null themselves; the few that
 *  don't (visualization surfaces) handle null gracefully. */
export function computeSignals(
  visits: VisitRow[],
  churnPct: number | null,
  conversionPct: number | null,
  noShowCount: number,
  targetHoursPerWeek: number | null,
): CoachingSignals {
  let quality1 = 0;
  let quality2 = 0;
  let quality3 = 0;
  let qualitySum = 0;
  let qualitySamples = 0;
  let totalMinutes = 0;
  let totalPauseCount = 0;
  let totalRevenue = 0;
  let totalTips = 0;
  let tipVisits = 0;
  let addonVisits = 0;
  let walkinCount = 0;
  let appointmentCount = 0;
  let consultSecSum = 0;
  let consultSamples = 0;
  let cashRevenue = 0;
  let paymentTagged = 0;
  // Discount accumulator — sum of (default_price - price) across
  // visits where default > price (only count actual discounts, not
  // upcharges). The denominator for the % is totalRevenue.
  let discountGivenCents = 0;
  const distinctNewClients = new Set<string>();
  // For cut-time variance we keep a running list of minutes per cut.
  // Computing stddev requires two passes (mean first, then squared
  // deviations) so the list stays in scope after the main loop.
  const cutMinutes: number[] = [];
  // For service-mix concentration we tally cuts per cut_type. Strip
  // the "+ addon" tail so "Fade" and "Fade + Beard" roll up together
  // — that's how the rest of the recap math handles it.
  const cutTypeCounts: Record<string, number> = {};

  for (const v of visits) {
    if (v.quality === 1) quality1 += 1;
    else if (v.quality === 2) quality2 += 1;
    else if (v.quality === 3) quality3 += 1;
    if (v.quality != null) {
      qualitySum += v.quality;
      qualitySamples += 1;
    }
    totalMinutes += v.duration_min ?? 0;
    if ((v.duration_min ?? 0) > 0) cutMinutes.push(v.duration_min!);
    totalPauseCount += v.pause_count ?? 0;
    totalRevenue += v.price_cents ?? 0;
    totalTips += v.tip_cents ?? 0;
    if ((v.tip_cents ?? 0) > 0) tipVisits += 1;
    if (Array.isArray(v.addons) && v.addons.length > 0) addonVisits += 1;
    if (v.was_appointment === true) appointmentCount += 1;
    else if (v.was_appointment === false) walkinCount += 1;
    if (v.client_id) distinctNewClients.add(v.client_id);
    if (v.payment_method != null) {
      paymentTagged += 1;
      if (v.payment_method === 'cash') cashRevenue += v.price_cents ?? 0;
    }
    // Discount-given accumulator. Only counts visits where default
    // exceeded the actual price (upcharges don't reduce revenue, so
    // they don't show up as "money left on the table").
    if (
      v.default_price_cents != null &&
      v.price_cents != null &&
      v.default_price_cents > v.price_cents
    ) {
      discountGivenCents += v.default_price_cents - v.price_cents;
    }
    // Consult phase time — heuristic match on phase name. The cut
    // timer's catalog uses names like "Consult / check-in", "Wash",
    // etc.; matching on the substring "consult" catches the right
    // entry across customizations. Skipped phases (seconds = 0,
    // skipped = true) are excluded so we measure actual consult
    // time, not the count of cuts that have a consult phase listed.
    if (Array.isArray(v.phase_times)) {
      for (const p of v.phase_times) {
        if (p.skipped || p.seconds <= 0) continue;
        if (/consult|check.?in|intake/i.test(p.name)) {
          consultSecSum += p.seconds;
          consultSamples += 1;
        }
      }
    }
    // Cut-type tally — split off the " + addon" suffix so the rollup
    // matches the recap's `top_cut_types` math (computeRecap in
    // packages/core/src/recap.ts uses the same first-segment split).
    if (v.cut_type) {
      const name = v.cut_type.split(' + ')[0]?.trim() || v.cut_type;
      cutTypeCounts[name] = (cutTypeCounts[name] ?? 0) + 1;
    }
  }

  const totalVisits = visits.length;
  const avgQuality = qualitySamples > 0 ? qualitySum / qualitySamples : null;
  const nailedItPct = qualitySamples > 0 ? (quality3 / qualitySamples) * 100 : null;
  const offDayPct = qualitySamples > 0 ? (quality1 / qualitySamples) * 100 : null;
  const tagged = walkinCount + appointmentCount;

  // Cut-time coefficient of variation = stddev ÷ mean. Sample stddev
  // (n-1) is the right denominator for "what's the inconsistency in
  // my cut times" — population would understate when the lookback
  // is small. Null when fewer than 5 cuts have a duration on file.
  let cutTimeCv: number | null = null;
  if (cutMinutes.length >= 5) {
    const mean = cutMinutes.reduce((a, b) => a + b, 0) / cutMinutes.length;
    const variance = cutMinutes.reduce(
      (acc, m) => acc + (m - mean) ** 2,
      0,
    ) / (cutMinutes.length - 1);
    const stddev = Math.sqrt(variance);
    cutTimeCv = mean > 0 ? stddev / mean : null;
  }

  // Top cut type — pick the highest count + compute its share of the
  // total. Null when no cut types were named (all anonymous walk-ins
  // saved without a label).
  let topCutTypeName: string | null = null;
  let topCutTypeCount = 0;
  let cutTypesTotal = 0;
  for (const [name, count] of Object.entries(cutTypeCounts)) {
    cutTypesTotal += count;
    if (count > topCutTypeCount) {
      topCutTypeCount = count;
      topCutTypeName = name;
    }
  }
  const topCutTypePct =
    cutTypesTotal > 0 ? (topCutTypeCount / cutTypesTotal) * 100 : null;

  // Utilization % — chair time as a fraction of target hours over the
  // lookback. LOOKBACK_DAYS / 7 weeks × target hours × 60 minutes is
  // the target minutes; chair_time_min was already summed into
  // totalMinutes above. Null when target isn't set.
  let utilizationPct: number | null = null;
  if (targetHoursPerWeek != null && targetHoursPerWeek > 0) {
    const targetMinutes = (LOOKBACK_DAYS / 7) * targetHoursPerWeek * 60;
    utilizationPct = targetMinutes > 0 ? (totalMinutes / targetMinutes) * 100 : null;
  }
  // Discount given as % of revenue. Null when revenue is 0.
  const discountGivenPct =
    totalRevenue > 0 ? (discountGivenCents / totalRevenue) * 100 : null;

  return {
    totalVisits,
    avgQuality,
    nailedItPct,
    offDayPct,
    qualitySampleSize: qualitySamples,
    avgCutMinutes: totalVisits > 0 ? totalMinutes / totalVisits : null,
    avgPausePerCut: totalVisits > 0 ? totalPauseCount / totalVisits : null,
    churnPct,
    conversionPct,
    recentNewClientCount: distinctNewClients.size,
    noShowCount,
    avgTicketCents: totalVisits > 0 ? Math.round(totalRevenue / totalVisits) : null,
    tipRatePct: totalRevenue > 0 ? (totalTips / totalRevenue) * 100 : null,
    tipAttachRatePct: totalVisits > 0 ? (tipVisits / totalVisits) * 100 : null,
    walkinPct: tagged > 0 ? (walkinCount / tagged) * 100 : null,
    walkinAppointmentTagged: tagged,
    addonAttachPct: totalVisits > 0 ? (addonVisits / totalVisits) * 100 : null,
    avgConsultSec: consultSamples > 0 ? consultSecSum / consultSamples : null,
    consultSampleSize: consultSamples,
    cutTimeCv,
    topCutTypePct,
    topCutTypeName,
    cashPct: totalRevenue > 0 && paymentTagged > 0 ? (cashRevenue / totalRevenue) * 100 : null,
    paymentTagged,
    utilizationPct,
    discountGivenPct,
  };
}

// ────────── Hook ──────────

/** Returns the top-priority coaching insight matching the user's
 *  current signals, plus the full signal bundle (exposed so the tile
 *  can render numeric context alongside the insight copy). Returns
 *  `insight: null` when not enough data to trigger any rule (under
 *  MIN_VISITS_FOR_INSIGHTS recent visits). */
export function useCoachingInsights(): {
  insight: CoachingInsight | null;
  signals: CoachingSignals | null;
  loading: boolean;
} {
  const recent = useRecentVisits(LOOKBACK_DAYS);
  const churn = useClientChurn();
  const conversion = useRegularConversion();
  const noShows = useNoShows();
  const profile = useCurrentUserProfile();

  const signals = useMemo(() => {
    if (recent.loading) return null;
    if (!recent.data) return null;
    return computeSignals(
      recent.data,
      churn.data?.churn_pct ?? null,
      conversion.data?.conversion_pct ?? null,
      noShows.data?.length ?? 0,
      profile.data?.target_hours_per_week ?? null,
    );
  }, [recent.loading, recent.data, churn.data, conversion.data, noShows.data, profile.data]);

  const insight = useMemo<CoachingInsight | null>(() => {
    if (!signals) return null;
    if (signals.totalVisits < MIN_VISITS_FOR_INSIGHTS) return null;
    const matches: CoachingInsight[] = [];
    for (const rule of RULES) {
      const result = rule(signals);
      if (result) matches.push(result);
    }
    matches.sort((a, b) => b.priority - a.priority);
    return matches[0] ?? null;
  }, [signals]);

  return {
    insight,
    signals,
    loading: recent.loading || churn.loading || conversion.loading || noShows.loading,
  };
}
